/** Public Industry Profile editor.
 *
 *  This is the user's "office / shop window" inside the ecosystem.
 *  They decide what's visible to industry peers via a per-field
 *  visibility toggle; the backend redacts hidden fields for anyone
 *  who isn't the owner. Live preview shows exactly what peers see.
 */
import { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff, Save, Store } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const FIELD_META = {
  bio:                 { label: "Bio",                 hint: "1-2 lines about your craft.",           multi: true },
  reel_url:            { label: "Showreel URL",        hint: "YouTube / Vimeo / MP4." },
  headshot_url:        { label: "Headshot URL",        hint: "Professional headshot." },
  past_credits:        { label: "Past Credits",        hint: "Comma-separated project titles.",       multi: true },
  contact_preferences: { label: "Contact Preferences", hint: "How you prefer to be reached.",         multi: true },
  phone:               { label: "Phone",               hint: "Only visible if you toggle it on." },
  email:               { label: "Email",               hint: "Only visible if you toggle it on." },
  location:            { label: "Base Location",       hint: "City / region you work out of." },
  languages:           { label: "Languages",           hint: "Comma-separated." },
  showreel_urls:       { label: "Additional Reels",    hint: "Comma-separated URLs." },
};

export default function PublicProfileCard({ user }) {
  const [fields, setFields]         = useState([]);
  const [values, setValues]         = useState({});
  const [visibility, setVisibility] = useState({});
  const [saving, setSaving]         = useState(false);

  const load = async () => {
    try {
      const [meta, mine] = await Promise.all([
        api.get("/public-profile/fields"),
        api.get("/users/me/public-profile"),
      ]);
      setFields(meta.data.fields || []);
      setValues(mine.data.values || {});
      setVisibility(mine.data.visibility || {});
    } catch (e) { console.warn("[public profile]", e?.message); }
  };
  useEffect(() => { void load(); }, []);

  const previewValues = useMemo(() => {
    const out = {};
    Object.keys(values).forEach((k) => { if (visibility[k]) out[k] = values[k]; });
    return out;
  }, [values, visibility]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/public-profile", { values, visibility });
      toast.success("Public profile updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="cs-card rounded-2xl p-6" data-testid="public-profile-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/55 inline-flex items-center gap-1">
            <Store size={11}/> Public Industry Profile
          </div>
          <div className="font-display text-xl uppercase tracking-tight">
            Your office window
          </div>
          <div className="text-xs text-white/50 mt-1 max-w-xl">
            Every field has an eye toggle — hide anything you don&apos;t want peers to see.
            Only industry insiders inside our ecosystem can view this.
          </div>
        </div>
        <Button onClick={save} disabled={saving}
                className="h-9 gap-1.5 bg-white text-black hover:bg-white/90"
                data-testid="profile-save-btn">
          <Save size={13}/> {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Editor */}
        <div className="space-y-3" data-testid="profile-editor">
          {fields.map((k) => {
            const meta = FIELD_META[k] || { label: k, hint: "" };
            const isVisible = !!visibility[k];
            const Input1 = meta.multi ? Textarea : Input;
            return (
              <div key={k} className="rounded-lg border border-white/10 bg-black/25 p-3"
                   data-testid={`profile-field-${k}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-[11px] tracking-[0.18em] uppercase text-white/70">
                    {meta.label}
                  </Label>
                  <button type="button"
                          onClick={() => setVisibility({ ...visibility, [k]: !isVisible })}
                          className={"h-7 px-2 rounded-md border text-[10px] tracking-[0.18em] uppercase inline-flex items-center gap-1 " +
                                     (isVisible
                                       ? "border-emerald-400/40 text-emerald-300"
                                       : "border-white/15 text-white/45 hover:text-white")}
                          data-testid={`profile-visibility-${k}`}>
                    {isVisible ? <><Eye size={11}/> Visible</> : <><EyeOff size={11}/> Hidden</>}
                  </button>
                </div>
                <Input1
                  value={values[k] ?? ""}
                  onChange={(e) => setValues({ ...values, [k]: e.target.value })}
                  placeholder={meta.hint}
                  className="bg-black/40 border-white/15 text-sm"
                  data-testid={`profile-input-${k}`}
                />
              </div>
            );
          })}
        </div>

        {/* Live peer preview */}
        <div className="rounded-xl border border-emerald-400/25 bg-emerald-400/[0.04] p-4"
             data-testid="profile-preview">
          <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-300 mb-3">
            Peer preview · what industry insiders see
          </div>
          <div className="space-y-2">
            <div className="text-lg font-display uppercase tracking-tight">{user?.name}</div>
            {Object.keys(previewValues).length === 0 ? (
              <div className="text-sm text-white/45">
                Nothing visible yet — flip the eye toggles to reveal fields.
              </div>
            ) : Object.keys(previewValues).map((k) => (
              <div key={k} data-testid={`preview-${k}`}>
                <div className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                  {(FIELD_META[k] || {}).label || k}
                </div>
                <div className="text-sm text-white/85 whitespace-pre-wrap break-words">
                  {String(previewValues[k])}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
